import { useCallback, useEffect, useState } from "react";
import { api, type BillingPlan } from "./api";
import { useT } from "./i18n";

/**
 * Active billing plans, ordered the way the backend wants them shown.
 * Shared by the paywall and the plan picker so both offer the same list —
 * `plans` is null while loading, `reload` retries after an error.
 */
export function useBillingPlans() {
  const t = useT();
  const [plans, setPlans] = useState<BillingPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(
    (isStale: () => boolean) =>
      api
        .billingPlans()
        .then((res) => {
          if (isStale()) return;
          const sorted = [...res.plans].sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
          );
          setPlans(sorted.filter((p) => p.is_active !== false));
          setError(null);
        })
        .catch(() => {
          if (isStale()) return;
          // Raw fetch / backend messages are English ("Failed to fetch") —
          // always show the localized wording instead.
          setError(t("settings.plansLoadFailed"));
        }),
    [t],
  );

  useEffect(() => {
    let cancelled = false;
    void fetchPlans(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchPlans]);

  const reload = useCallback(() => {
    void fetchPlans(() => false);
  }, [fetchPlans]);

  return { plans, error, reload };
}
